import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiArrowLeft, FiUploadCloud, FiStar, FiX } from "react-icons/fi";
import api, { imgUrl } from "../utils/api";
import Loader from "../components/Loader";

const emptyForm = {
  name: "",
  location: "",
  text: "",
  rating: 5,
  service: "",
  image: "",
  approved: true,
  order: 0,
};

const inputClass =
  "w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400";

export default function TestimonialForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api
        .get(`/testimonials/admin/edit/${id}`)
        .then((res) => setForm({ ...emptyForm, ...res.data }))
        .catch(() => toast.error("Failed to load testimonial"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({ ...form, image: data.url });
      toast.success("Photo uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) {
      toast.error("Name and review text are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      rating: Number(form.rating) || 5,
      order: Number(form.order) || 0,
    };
    try {
      if (isEdit) {
        await api.put(`/testimonials/admin/${id}`, payload);
        toast.success("Testimonial updated");
      } else {
        await api.post("/testimonials/admin", payload);
        toast.success("Testimonial created");
      }
      navigate("/admin/testimonials");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save testimonial");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="max-w-2xl">
      <Link
        to="/admin/testimonials"
        className="inline-flex items-center gap-1 text-saffron-600 text-sm font-medium mb-6 hover:gap-2 transition-all"
      >
        <FiArrowLeft /> Back to Testimonials
      </Link>

      <h1 className="font-heading text-2xl text-maroon-800 mb-6">
        {isEdit ? "Edit Testimonial" : "Add New Testimonial"}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Devotee Name *</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className={inputClass}
            placeholder="e.g. Mrs. Sunita Patil"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              name="location"
              value={form.location}
              onChange={handleChange}
              className={inputClass}
              placeholder="e.g. Thane West"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service (optional)</label>
            <input
              type="text"
              name="service"
              value={form.service}
              onChange={handleChange}
              className={inputClass}
              placeholder="e.g. Satyanarayan Mahapuja"
            />
          </div>
        </div>

        {/* Star rating picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setForm({ ...form, rating: n })}
                className="p-1"
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
              >
                <FiStar
                  size={26}
                  className={n <= form.rating ? "text-gold-500" : "text-gray-300"}
                  fill={n <= form.rating ? "currentColor" : "none"}
                />
              </button>
            ))}
            <span className="ml-2 text-sm text-gray-500">{form.rating} / 5</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Review Text *</label>
          <textarea
            name="text"
            value={form.text}
            onChange={handleChange}
            rows={4}
            className={inputClass}
            placeholder="What did the devotee say about the service?"
          />
        </div>

        {/* Optional photo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Devotee Photo (optional)</label>
          {form.image ? (
            <div className="flex items-center gap-3 mb-2">
              <div className="relative w-16 h-16 rounded-full overflow-hidden border border-gray-200">
                <img src={imgUrl(form.image)} alt="devotee" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, image: "" })}
                  className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"
                >
                  <FiX size={12} />
                </button>
              </div>
              <span className="text-xs text-gray-500">Photo uploaded</span>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
              <FiUploadCloud /> {uploading ? "Uploading..." : "Upload Photo"}
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
          <input
            type="number"
            name="order"
            value={form.order}
            onChange={handleChange}
            className={inputClass}
            placeholder="0"
          />
          <p className="text-xs text-gray-400 mt-1">Lower numbers appear first.</p>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            name="approved"
            checked={form.approved}
            onChange={handleChange}
            className="w-4 h-4"
          />
          Approved (visible on the public website)
        </label>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 bg-maroon-700 hover:bg-maroon-800 disabled:opacity-60 text-gold-100 px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          <FiSave /> {saving ? "Saving..." : "Save Testimonial"}
        </button>
      </form>
    </div>
  );
}
